<template>
    <div class="import-step-setup">
        <CompassAlert
            class="mb-4"
            :description="$t.__('settings.import.ImportSetupStep.alert')"
            :showClose="false"
        />
        <div class="inner-stepper">
            <div class="inner-step-choice">
                <div
                    v-for="step in stepper"
                    :key="step.id"
                    class="inner-step-item"
                    :class="{ active: step.id === getCurrentSubStep.id, valid: step.isValid }"
                    @click="onClickInnerStepItem(step)"
                >
                    <span v-if="step.isValid && step.id !== getCurrentSubStep.id" class="check">
                        <CompassIcon type="success" icon="check" />
                    </span>
                    <span v-else class="number">
                        {{ step.id }}
                    </span>
                    <div class="inner-step-item-description">
                        <h4 class="title typographies-heading-h4">{{ step.title }}</h4>
                        <p class="subtitle typographies-paragraph-small-regular">
                            {{ step.subTitle }}
                        </p>
                    </div>
                </div>
            </div>
            <Transition name="fade" mode="out-in">
                <component :is="innerStepComponent" class="inner-step-content" />
            </Transition>
        </div>
    </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
const importsStore = createNamespacedHelpers('importsStore');
import identifiersComponent from 'vcomponents/settings/import/setupInnerStep/identifiersComponent.vue';
import parametersComponent from 'vcomponents/settings/import/setupInnerStep/parametersComponent.vue';
import cfParametersComponent from 'vcomponents/settings/import/setupInnerStep/cfParametersComponent.vue';
export default {
    props: {},
    data() {
        return {
            compareKeyRadio: 'id',
            defaultOptions: {},
            isCustomFieldsEnabled: false,
            stepper: [],
        };
    },
    computed: {
        ...importsStore.mapGetters([
            'getOptionsSelectedImport',
            'getTypeImport',
            'getImportSteps',
            'getCurrentStepId',
            'getCurrentSubStepId',
            'getCurrentSubStep',
            'getCurrentStep',
            'getHasCustomFields',
        ]),
        innerStepComponent() {
            switch (this.getCurrentSubStepId) {
                case 2:
                    return parametersComponent;
                case 3:
                    return cfParametersComponent;
                case 1:
                default:
                    return identifiersComponent;
            }
        },
    },
    created() {
        this.stepper = this.getCurrentStep.subSteps;
        if (!this.getHasCustomFields) {
            this.stepper = this.stepper.filter((step) => step.id !== 3);
        }
        this.updateCurrentSubStepId(1);
    },
    methods: {
        ...importsStore.mapActions(['updateCurrentSubStepId', 'updateHasCustomFields']),
        onClickInnerStepItem(step) {
            if (!step.isValid) {
                return;
            }
            this.updateCurrentSubStepId(step.id);
        },
    },
};
</script>
<style scoped lang="scss">
@use '~@sellsy/styleguide/src/_variables' as *;

.setup-import {
    .import-step-setup {
        display: flex;
        flex-direction: column;
        height: 100%;

        h1 {
            line-height: 29px;
        }

        h1,
        h2,
        h3,
        h4 {
            margin: 0;
        }

        p {
            margin: 0;
        }

        .inner-stepper {
            display: flex;
            flex: 1;
            gap: 40px;
            padding: 0 0 var(--spacing-6);

            .inner-step-choice {
                display: flex;
                flex-direction: column;
                gap: 8px;

                .inner-step-item {
                    cursor: default;

                    display: flex;
                    flex-direction: row;
                    gap: 16px;
                    align-items: center;

                    width: 300px;
                    padding: 16px;

                    border-radius: 4px;

                    &.active {
                        background: var(--colors-component-background-actionable-active-default);

                        .number {
                            padding: var(--spacing-1) 16.5px;
                            color: var(--colors-common-text-contrast-default);
                            background: var(--colors-common-background-contrast-hover);
                        }

                        .inner-step-item-description {
                            .title {
                                color: var(--colors-common-text-contrast-default);
                            }

                            .subtitle {
                                color: var(--colors-common-text-contrast-caption);
                            }
                        }

                        &:hover {
                            background: var(--colors-component-background-actionable-active-hover);
                        }
                    }

                    &.valid:not(.active) {
                        cursor: pointer;

                        .check {
                            background-color: var(--colors-common-background-success-subtle);
                        }

                        .inner-step-item-description {
                            .title {
                                color: var(--colors-common-text-standard-default);
                            }

                            .subtitle {
                                color: var(--colors-common-text-standard-caption);
                            }
                        }

                        &:hover {
                            background-color: var(--colors-component-background-actionable-hover);
                        }
                    }
                }

                span {
                    display: flex;
                    align-items: center;
                    justify-content: center;

                    width: 40px;
                    height: 40px;

                    font-size: 17px;
                    font-weight: 700;
                    line-height: 24px;
                    color: var(--colors-common-text-standard-disabled);

                    background: var(--colors-common-background-disabled);
                    border-radius: 50px;
                }

                .inner-step-item-description {
                    display: flex;
                    flex-direction: column;

                    .title,
                    .subtitle {
                        color: var(--colors-common-text-standard-disabled);
                    }
                }
            }
        }

        .inner-step-content::v-deep {
            display: flex;
            flex-direction: column;
            padding-left: 40px;
            border-left: 1px solid var(--colors-common-border-lighter);

            .title {
                margin: 4px 0 0;
            }

            .subtitle {
                max-width: 659px;
                margin: 0 0 var(--spacing-4);
                padding-bottom: var(--spacing-3);

                color: var(--colors-common-text-standard-caption);

                border-bottom: 1px solid var(--colors-common-border-lighter);
            }

            .parameter-container,
            .cf-container {
                display: flex;
                flex-direction: column;
                gap: var(--spacing-4);

                .parameter-item,
                .cf-item {
                    width: 460px;

                    .compass-select {
                        width: 100%;
                    }
                }
            }

            .parameter-title,
            .cf-title {
                display: flex;
                gap: var(--spacing-1);
                align-items: center;
                margin-bottom: var(--spacing-0);
            }
        }
    }

    .separator {
        width: 100%;
        height: 1px;
        margin: var(--spacing-5) 0;
        background-color: $gray-05;
    }

    .compass-select::v-deep {
        width: 460px;
    }

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 120ms cubic-bezier(0, 0, 0.3, 1);
    }

    .fade-enter-from,
    .fade-leave-to {
        opacity: 0;
    }
}
</style>
